import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import "./FoodAllowance.css";
import { toast } from "react-toastify";

const UpdateFoodDetail = ({
  foodRecord,
  foodDetail,
  companyId,
  onClose,
  onUpdate,
  fetchFoodAllowance,
}) => {
  // console.log("update food", foodRecord, foodDetail);

  // Initialize form data.
  const initialFormData = {
    isEaten: foodDetail.isEaten || false,
    dish: foodDetail.dish || "",
    quantity: foodDetail.quantity || "",
    hours: foodDetail.hours || "",
    date: foodDetail.date
      ? new Date(foodDetail.date).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    // Use vessel _id if available; otherwise, use vesselId. If none exists, default to null.
    vessalId:
      foodDetail.vessal && (foodDetail.vessal._id || foodDetail.vessal.vesselId)
        ? foodDetail.vessal._id || foodDetail.vessal.vesselId
        : null,
    amount: foodDetail.amount || "",
    allowance: foodDetail.allowance || "",
    remark: foodDetail.remark || "",
    // Pre-populate vesselSearch with vessel name and code if available.
    vesselSearch: foodDetail.vessal
      ? `${foodDetail.vessal.vessel_name} - ${foodDetail.vessal.vessel_code}`
      : "",
    showVesselDropdown: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vesselError, setVesselError] = useState("");

  // Fetch vessels for vessel selection using the companyId.
  useEffect(() => {
    const fetchVessels = async () => {
      try {
        const response = await axiosInstance.get(
          `/invoice/vessal/${companyId}`,
        );
        setVessels(response.data.vessels);
      } catch (error) {
        toast.error("Error fetching vessel data");
      }
    };
    fetchVessels();
  }, [companyId]);

  // Compute the selected vessel based on formData.vessalId.
  const selectedVessel = vessels.find((v) => v._id === formData.vessalId);

  // Prevent background scrolling when the modal is open.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Close modal when clicking outside the modal dialog.
  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  // Sanitize form data by removing temporary keys.
  const sanitizeFormData = (data) => {
    const { vesselSearch, showVesselDropdown, ...rest } = data;
    return rest;
  };

  // Handle input changes.
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "vesselSearch") {
      setFormData((prev) => ({
        ...prev,
        vesselSearch: value,
        // Reset vessalId if search term changes.
        vessalId: null,
      }));
      setVesselError(""); // Clear error as user types
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Handle form submission.
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If food is eaten, ensure a vessel is selected.
    if (formData.isEaten && !formData.vessalId) {
      setVesselError("Please select a vessel.");
      toast.error("Please select a vessel.");
      return;
    }
    setLoading(true);
    try {
      const payload = sanitizeFormData(formData);
      const response = await axiosInstance.patch(
        `/foodallowance/${foodRecord._id}/${foodDetail._id}`,
        payload,
      );
      toast.success("Food detail updated successfully");
      if (onUpdate) await onUpdate(); // re-fetch the latest data
      onClose();
    } catch (error) {
      // console.error("Error updating food detail:", error.response);
      let errorMessage = "Error updating food detail";
      if (error.response?.data?.message) {
        errorMessage = Array.isArray(error.response.data.message)
          ? error.response.data.message.join(", ")
          : error.response.data.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      onClick={handleOutsideClick}
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content foodallowance_model">
          <div className="modal-header text-white text-center vesel_form">
            <h5 className="modal-title">Update Food Detail</h5>
            <button
              type="button"
              className="btn-close text-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              {/* Date Field */}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                {/* Vessel Selection */}
                <div className="col-md-4  position-relative">
                  <label className="form-label">Vessel</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type vessel name or code"
                    name="vesselSearch"
                    value={
                      (selectedVessel && selectedVessel.vessel_name) ||
                      formData.vesselSearch ||
                      ""
                    }
                    onChange={handleChange}
                    onFocus={() =>
                      setFormData((prev) => ({
                        ...prev,
                        showVesselDropdown: true,
                      }))
                    }
                    required
                  />
                  {formData.showVesselDropdown && (
                    <ul
                      className="list-group position-absolute"
                      style={{
                        zIndex: 1200,
                        width: "100%",
                        maxHeight: 220,
                        overflowY: "auto",
                      }}
                    >
                      {vessels
                        .filter(
                          (item) =>
                            item.vessel_name
                              .toLowerCase()
                              .includes(
                                formData.vesselSearch?.toLowerCase() || "",
                              ) ||
                            item.vessel_code
                              .toLowerCase()
                              .includes(
                                formData.vesselSearch?.toLowerCase() || "",
                              ),
                        )
                        .map((item) => (
                          <li
                            key={item._id}
                            className="list-group-item list-group-item-action"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                vessalId: item._id,
                                vesselSearch: "",
                                showVesselDropdown: false,
                              }));
                              setVesselError(""); // clear any existing error
                            }}
                          >
                            {`${item.vessel_name} - ${item.vessel_code}`}
                          </li>
                        ))}
                      {vessels.filter(
                        (item) =>
                          item.vessel_name
                            .toLowerCase()
                            .includes(
                              formData.vesselSearch?.toLowerCase() || "",
                            ) ||
                          item.vessel_code
                            .toLowerCase()
                            .includes(
                              formData.vesselSearch?.toLowerCase() || "",
                            ),
                      ).length === 0 && (
                        <li className="list-group-item list-group-item-action">
                          No results found
                        </li>
                      )}
                    </ul>
                  )}
                  {selectedVessel && (
                    <div className="selected-vessel mt-2">
                      <strong>Selected Vessel:</strong>{" "}
                      {selectedVessel.vessel_name} -{" "}
                      {selectedVessel.vessel_code}
                    </div>
                  )}
                  {vesselError && (
                    <div style={{ color: "red", marginTop: "5px" }}>
                      {vesselError}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    name="hours"
                    value={formData.hours}
                    onChange={handleChange}
                    required={formData.isEaten}
                    min="0"
                    step="any"
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Allowance</label>
                  <input
                    type="number"
                    className="form-control"
                    name="allowance"
                    value={formData.allowance}
                    onChange={handleChange}
                    required={formData.isEaten}
                    min="0"
                    step="any"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Remark</label>
                  <textarea
                    type="text"
                    className="form-control"
                    name="remark"
                    value={formData.remark}
                    onChange={handleChange}
                    required={formData.isEaten}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-center">
                  <label className="form-label  mb-5 me-2">Is Eaten?</label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      name="isEaten"
                      checked={formData.isEaten}
                      onChange={handleChange}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
              {formData.isEaten && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Dish</label>
                      <input
                        type="text"
                        className="form-control"
                        name="dish"
                        value={formData.dish}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        required
                        min="0"
                        step="any"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Amount</label>
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        required
                        min="0"
                        step="any"
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="mt-4 text-center">
                <button type="submit" className="button-2" disabled={loading}>
                  {loading ? "Updating..." : "Update Food Detail"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateFoodDetail;
